import React from 'react';
import { useTranslation } from 'react-i18next';
import './StructuredExplanation.css';

const getRiskColorByLabel = (riskLabel) => {
    if (!riskLabel) return '#8BC34A';
    const lowerLabel = riskLabel.toLowerCase();
    if (lowerLabel.includes('severe')) return '#F44336';
    if (lowerLabel.includes('high')) return '#FF9800';
    if (lowerLabel.includes('moderate')) return '#FFC107';
    if (lowerLabel.includes('minor')) return '#8BC34A'; 
    if (lowerLabel.includes('low')) return '#8BC34A';
    return '#4CAF50'; 
};

// --- Context Logic: Determines which factor is dominant ---
const getResultContext = (score1, score2) => {
    const THRESHOLD = 50;

    // 1. Mixed: Both are significant
    if (score1 >= THRESHOLD && score2 >= THRESHOLD) {
        return 'mixed';
    }
    
    // 2. Factor 2 Dominant
    if (score2 >= THRESHOLD) {
        return 'factor2_dominant';
    }

    // 3. Factor 1 Dominant
    if (score1 >= THRESHOLD) {
        return 'factor1_dominant';
    }

    // 4. Low Scores (<50): Check which is higher to give relevant "Safe" message
    if (score2 > score1) {
        return 'factor2_dominant';
    } else {
        return 'factor1_dominant';
    }
};

const BoldParser = ({ text }) => {
    if (!text) return null;
    const parts = text.split('**');
    return (
        <>
            {parts.map((part, index) =>
                index % 2 === 1 ? <strong key={index}>{part}</strong> : part
            )}
        </>
    );
};

const StructuredExplanation = ({ data, score, taskKey = 'digital_integrity_score' }) => {
    const { t } = useTranslation();

    if (!data || typeof data !== 'object') {
        return <p className="structured-explanation-fallback">{data}</p>;
    }
    
    // --- FIX: Extract factors dynamically ---
    // Convert the contributing_factors object into an array of entries [ [key, value], [key, value] ]
    const factorEntries = Object.entries(data.contributing_factors || {});
    
    // Safety check: ensure we actually have two factors
    if (factorEntries.length < 2) {
         return <p className="structured-explanation-fallback">Error rendering structured explanation: Missing dependencies.</p>;
    }

    const [, f1Data] = factorEntries[0];
    const [, f2Data] = factorEntries[1];

    const f1Level = f1Data.level || f1Data.risk || 'minimal';
    const f2Level = f2Data.level || f2Data.risk || 'minimal';
    const score1 = f1Data.score || 0;
    const score2 = f2Data.score || 0;

    // 2. Determine Context
    const contextKey = getResultContext(score1, score2);

    // 3. Get Overall Verdict Key (e.g., severe, high, moderate)
    const verdictKey = data.overall_verdict || data.recommendation_key || 'minimal';

    // 4. Construct Keys
    // Summary now depends on the dynamic taskKey, Context + Verdict
    const summaryText = t(`metaScores.${taskKey}.summary.${contextKey}.${verdictKey}`, { score, defaultValue: '' });
    const recommendationText = t(`metaScores.${taskKey}.recommendation.${contextKey}.${verdictKey}`, { defaultValue: '' });

    // 5. Translate Factor Labels
    const f1LevelKey = f1Level.toLowerCase().replace(' ', '_');
    const f2LevelKey = f2Level.toLowerCase().replace(' ', '_');
    
    const translatedF1Level = t(`riskLevels.${f1LevelKey}`, f1Level);
    const translatedF2Level = t(`riskLevels.${f2LevelKey}`, f2Level);

    return (
        <div className="structured-explanation">
            <ul>
                {/* Dynamic Summary based on Context */}
                {summaryText && (
                    <li>
                        <BoldParser text={summaryText} />
                    </li>
                )}
                
                <li>
                    <strong>{t(`metaScores.${taskKey}.contributingFactors.title`, { defaultValue: t('metaScores.default.contributingFactors.title') })}</strong>
                    <ul>
                         <li style={{ color: getRiskColorByLabel(f1Level) }}>
                            <BoldParser text={t(`metaScores.${taskKey}.contributingFactors.factor1Label`, { 
                                level: translatedF1Level, 
                                score: score1 
                            })} />
                        </li>
                         <li style={{ color: getRiskColorByLabel(f2Level) }}>
                            <BoldParser text={t(`metaScores.${taskKey}.contributingFactors.factor2Label`, {  
                                level: translatedF2Level, 
                                score: score2 
                            })} />
                        </li>
                    </ul>
                </li>

                {recommendationText && (
                    <li>
                         <BoldParser text={recommendationText} />
                    </li>
                )}
            </ul>
        </div>
    );
};

export default StructuredExplanation;